import { useEffect, useState, CSSProperties } from 'react';
import { WEBSITE_EDITOR_TABS, WEBSITE_VIEW_ONLY_TABS } from './components/CustomizeWebsite/utils';
import { ScrollPosition } from "modules/Types";
import { WEBSITE_TABS_VIEW_ONLY } from "modules/Types";
import { WEBSITE_TABS } from "modules/Types";
import WebsitePreviewV2 from './components/WebsitePreview/WebsitePreviewV2';
import { useStyles } from './components/CustomizeWebsite/styles';
import MusicSiteSetupSection from './components/CustomizeWebsite/components/SetupSection/MusicSiteSetupSection';
import DataCollectorSetupSection from './components/CustomizeWebsite/components/SetupSection/DataCollectorSetupSection';
import TourSetupSection from './components/CustomizeWebsite/components/SetupSection/TourSetupSection';
import StyleSection from './components/CustomizeWebsite/components/StyleSection/StyleSection';
import useScreen from 'Hooks/useScreen';
import ConnectSection from './components/CustomizeWebsite/components/ConnectSection/ConnectSection';
import { usePalette } from 'react-palette';
import useCurrentBrand from 'Hooks/useCurrentBrand';
import { clsx, styx } from 'modules/Utils';
import { WEBSITE_BOTTOM_BAR_HEIGHT, USER_PERMISSION_ON_BRAND, WebsiteType } from 'modules/Const';
import { getUrlParam } from 'modules/Utils';
import useWebsite from 'Hooks/useWebsite';
import WebsiteAnalyticsTab from './components/CustomizeWebsite/components/WebsiteAnalyticsTab';
import TitleSetupView from './components/CustomizeWebsite/components/SetupSection/TitleSetupView';
import Loader from 'elements/Loader';
import TabsSymphony, { TabItem } from 'elements/TabsSymphony';

const { VIEW_ONLY } = USER_PERMISSION_ON_BRAND

const { DATA_COLLECTOR, RECORD_STREAMS, TOUR } = WebsiteType
const { ANALYTICS, CONNECT, SETUP, STYLE } = WEBSITE_TABS;

interface WebsiteProps {
    className?: string;
    style?: CSSProperties;
}

const WebsiteEditorV2 = ({
    className = '',
    style = {},
}: WebsiteProps) => {
    const { mobileView } = useScreen();
    const {
        brandContent,
        websiteType,
        youtubeVideoLink, setYTVideoLink,
        embedYt,
        mainColor = '',
        setMainColor,
        recordSelected,
        secondaryColor = '',
        setSecondaryColor,
        youtubeID, setYoutubeEmbedId,
        customThumbnailUrl,
    } = useWebsite();
    const { currentBrand } = useCurrentBrand();

    const classes = useStyles();
    const { mStyle } = useScreen();

    useEffect(() => {
        console.log('>> Website editor v2 did mount');
    }, []);

    const previewStateScrollPositionInitObj = {
        customize: 0,
        preview: 0,
    };


    const renderSetupSection = () => {
        let setupComponent = null;
        switch (websiteType) {
            case RECORD_STREAMS:
                setupComponent = <MusicSiteSetupSection />;
                break;
            case DATA_COLLECTOR:
                setupComponent = <DataCollectorSetupSection />;
                break;
            case TOUR:
                setupComponent = <TourSetupSection />;
                break;
        }

        if (!setupComponent) {
            return null;
        }

        return (
            <div
                className="musicSiteSetupSectionComponent"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: 16,
                }}
            >
                <TitleSetupView />
                {setupComponent}
            </div>
        );
    }

    const renderTabPanel = () => {
        switch (selectedWebsiteEditorTab.id) {
            case SETUP:
                return renderSetupSection()
            case STYLE:
                return <StyleSection />
            case ANALYTICS:
                return <WebsiteAnalyticsTab />
            case CONNECT:
                return <ConnectSection className="connectSectionFromWebsiteEditor" />
            default:
                return null
        }
    };

    const { published } = brandContent?.content_metadata || {};
    console.log('published', published);
    const { permissionLevel } = currentBrand || {};

    const tabs = Object.values(permissionLevel === VIEW_ONLY ? WEBSITE_TABS_VIEW_ONLY : WEBSITE_TABS);

    // Get tab from URL or fall back to default
    const tabParam = getUrlParam('tab')?.toString().toUpperCase();
    let defaultTab = tabParam && WEBSITE_TABS[tabParam as keyof typeof WEBSITE_TABS]
        ? WEBSITE_TABS[tabParam as keyof typeof WEBSITE_TABS]
        : (published ? ANALYTICS : SETUP);

    // Make sure we don't select Analytics tab if website is not published
    if (defaultTab === ANALYTICS && !published) {
        defaultTab = SETUP;
    }

    const [selectedWebsiteEditorTab, setSelectedWebsiteEditorTab] = useState<TabItem>(WEBSITE_EDITOR_TABS.find((tab) => tab.id === defaultTab) || WEBSITE_EDITOR_TABS[0]);

    // Update URL when tab changes
    const handleWebsiteEditorTabChange = (tab: TabItem) => {
        setSelectedWebsiteEditorTab(tab);
    };

    const [loadingRecordDetails, setLoadingRecordDetails] = useState<any>(false);
    const [previewState, setPreviewState] = useState(true);
    const [previewStateScrollPosition, setPreviewStateScrollPosition] = useState<ScrollPosition>(previewStateScrollPositionInitObj);
    const [validMainColor, setValidMainColor] = useState(true);
    const [validSecondaryColor, setValidSecondaryColor] = useState(true);

    const imageUrl = recordSelected?.thumbnail_url || currentBrand?.image || '';
    let paletteImageUrl = currentBrand?.image || '';
    if (websiteType === RECORD_STREAMS) {
        paletteImageUrl = recordSelected?.thumbnail_url || '';
    }

    const { data: paletteData } = usePalette(paletteImageUrl);

    useEffect(() => {
        if (customThumbnailUrl || imageUrl) {
            const paletteColors = Object.values(paletteData);

            const newMainColor = mainColor || paletteColors[0] || '';
            const newSecondaryColor = secondaryColor || paletteColors[1] || '';

            if (newMainColor !== mainColor) {
                setMainColor(newMainColor);
            }
            if (newSecondaryColor !== secondaryColor) {
                setSecondaryColor(newSecondaryColor);
            }
        }
    }, [paletteData, customThumbnailUrl, imageUrl, mainColor, secondaryColor]);

    useEffect(() => {
        if (previewState) {
            setPreviewStateScrollPosition((prevState) => ({
                ...prevState,
                customize: window.scrollY,
            }));
        } else {
            setPreviewStateScrollPosition((prevState) => ({
                ...prevState,
                preview: window.scrollY,
            }));
        }
    }, [previewState]);

    useEffect(() => {
        if (previewState) {
            window.scrollTo(0, 0);
        } else {
            window.scrollTo(0, previewStateScrollPosition.customize);
        }
    }, [previewStateScrollPosition]);

    useEffect(() => {
        if (youtubeVideoLink) {
            const validId = youtube_parser(youtubeVideoLink);
            if (validId) {
                setYoutubeEmbedId(validId);
            } else {
                setYoutubeEmbedId('');
            }
        } else {
            setYoutubeEmbedId('');
        }
    }, [youtubeVideoLink]);

    useEffect(() => {
        if (embedYt && !youtubeVideoLink && !youtubeID) {
            // default to the one linekd to the song
            const recordLinks = recordSelected?.external_links;
            if (recordLinks) {
                const yt = recordLinks.find((o: { name: string; url: string }) => {
                    return o.name === 'youtube';
                });
                if (yt) {
                    const defaultYouTubeId = youtube_parser(yt.url);
                    if (defaultYouTubeId) {
                        setYTVideoLink(yt.url);
                    }
                }
            }
        }
    }, [embedYt]);

    useEffect(() => {
        if (!/^#[0-9A-F]{6}$/i.test(mainColor)) {
            setValidMainColor(false);
        } else {
            setValidMainColor(true);
        }

        if (!/^#[0-9A-F]{6}$/i.test(secondaryColor)) {
            setValidSecondaryColor(false);
        } else {
            setValidSecondaryColor(true);
        }
    }, [mainColor, secondaryColor]);

    const youtube_parser = (url: string) => {
        var regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        var match = url.match(regExp);
        return match && match[7].length === 11 ? match[7] : false;
    }

    const renderCustomizeWebsiteTabs = () => {
        // Filter out Analytics tab if website is not published
        const filteredTabs = (permissionLevel === VIEW_ONLY ? WEBSITE_VIEW_ONLY_TABS : WEBSITE_EDITOR_TABS)
            .filter(tab => {
                // Only filter out Analytics tab when published is not true
                if (tab.id === ANALYTICS && !published) {
                    return false;
                }
                return true;
            });

        return (
            <TabsSymphony
                className="customizeWebsiteTabsSymphony"
                style={sty.customizeWebsiteTabsSymphony}
                tabs={filteredTabs}
                onChange={(tab) => handleWebsiteEditorTabChange(tab as TabItem)}
                selectedTab={selectedWebsiteEditorTab}
            />
        );
    };

    const renderLoadingState = () => {
        return (
            <div className='loadingRecordDetails flex-col flex items-center m-8 '>
                <Loader />
                <p className='mt-1'>Loading details for "{recordSelected?.name}"...</p>
            </div>
        )
    }

    // NOTE: render()
    return (
        <div
            className={clsx(
                'customizeWebsiteV2Component',
                className
            )}
            style={styx(
                sty.customizeWebsiteV2Component,
                style
            )}
        >
            <div className="websiteEditorPane" style={mStyle(sty, 'websiteEditorPane')}>
                {renderCustomizeWebsiteTabs()}
                {websiteType === RECORD_STREAMS && (!recordSelected?.full_info || loadingRecordDetails) ?
                    renderLoadingState()
                    :
                    renderTabPanel()
                }
            </div>
            {!mobileView &&
                <WebsitePreviewV2
                    className='websitePreviewV2Desktop'
                />
            }
        </div>
    );
}

const sty: Record<string, CSSProperties> = {
    customizeWebsiteV2Component: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        gap: 16,
        padding: 16,
    },
    websiteEditorPane: {
        display: 'flex',
        alignSelf: 'flex-start',
        maxWidth: 680,
        width: '100%',
        flexDirection: 'column',
        gap: 16,
        marginBottom: 0,
    },
    websiteEditorPane_mobile: {
        width: '100%',
        marginBottom: WEBSITE_BOTTOM_BAR_HEIGHT,
    },
    customizeWebsiteTabsSymphony: {
        display: 'flex',
        justifyContent: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: 8,
        paddingTop: 8
    }
}

export default WebsiteEditorV2;